<template>
    <el-dialog
        v-model="dialogVisible"
        :title="title"
        width="600px"
        :close-on-click-modal="false"
        @close="handleClose"
    >
        <el-form
            ref="formRef"
            :model="form"
            :rules="rules"
            label-width="100px"
        >
            <el-form-item :label="t('messageAdmin.messageType')" prop="type">
                <el-select v-model="form.type" :placeholder="t('messageAdmin.typePh')" style="width: 100%">
                    <el-option :label="t('messageAdmin.typeSystem')" :value="1" />
                    <el-option :label="t('messageAdmin.typeAnnouncement')" :value="3" />
                </el-select>
            </el-form-item>
            <el-form-item :label="t('messageAdmin.colTitle')" prop="title">
                <el-input v-model="form.title" :placeholder="t('messageAdmin.titlePh')" maxlength="100" show-word-limit />
            </el-form-item>
            <el-form-item :label="t('messageAdmin.colContent')" prop="content">
                <el-input
                    v-model="form.content"
                    type="textarea"
                    :rows="6"
                    :placeholder="t('messageAdmin.contentPh')"
                    maxlength="1000"
                    show-word-limit
                />
            </el-form-item>
        </el-form>
        <template #footer>
            <span class="dialog-footer">
                <el-button @click="handleClose">{{ t('common.cancel') }}</el-button>
                <el-button type="primary" @click="handleSubmit" :loading="loading">{{ t('common.confirm') }}</el-button>
            </span>
        </template>
    </el-dialog>
</template>

<script setup>
import { ref, reactive, watch, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import { saveMessageApi } from '@/api/message';
import { usePageI18n } from '@/composables/usePageI18n.js';
import { applyMessageFormRules } from '@/utils/messageListI18n.js';

const props = defineProps({
    title: {
        type: String,
        default: ''
    },
    row: {
        type: Object,
        default: () => ({})
    }
});

const emit = defineEmits(['refresh']);

const dialogVisible = ref(false);
const formRef = ref();
const loading = ref(false);

const form = reactive({
    id: null,
    type: null,
    title: '',
    content: '',
});

const rules = {
    type: [
        { required: true, message: '', trigger: 'change' }
    ],
    title: [
        { required: true, message: '', trigger: 'blur' },
        { min: 1, max: 100, message: '', trigger: 'blur' }
    ],
    content: [
        { required: true, message: '', trigger: 'blur' },
        { min: 1, max: 1000, message: '', trigger: 'blur' }
    ]
};

const applyLabels = () => {
    applyMessageFormRules(rules);
};
const { t } = usePageI18n(applyLabels);

watch(() => props.row, (newVal) => {
    if (newVal && Object.keys(newVal).length > 0) {
        form.id = newVal.id || null;
        form.type = newVal.type || null;
        form.title = newVal.title || '';
        form.content = newVal.content || '';
    } else {
        resetForm();
    }
}, { immediate: true, deep: true });

const resetForm = () => {
    form.id = null;
    form.type = null;
    form.title = '';
    form.content = '';
    formRef.value?.resetFields();
};

const open = () => {
    dialogVisible.value = true;
    if (props.row && props.row.id) {
        // edit mode
    } else {
        resetForm();
    }
};

const handleClose = () => {
    dialogVisible.value = false;
    resetForm();
};

const handleSubmit = async () => {
    if (!formRef.value) return;
    
    await formRef.value.validate((valid) => {
        if (valid) {
            loading.value = true;
            const submitData = {
                id: form.id,
                type: form.type,
                title: form.title,
                content: form.content,
            };
            
            saveMessageApi(submitData).then(res => {
                if (res.code === 200) {
                    ElMessage.success(t('common.success'));
                    handleClose();
                    emit('refresh');
                } else {
                    ElMessage.error(res.msg || t('common.failed'));
                }
            }).catch(err => {
                ElMessage.error(t('messageAdmin.retryFailed'));
                console.error(err);
            }).finally(() => {
                loading.value = false;
            });
        }
    });
};

onMounted(() => {
    applyLabels();
});

defineExpose({
    open
});
</script>

<style lang="less" scoped>
.dialog-footer {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}
</style>
